import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import EmptyRows from "../../../../components/reusableComponent/EmptyRows";
import TableModal from "../../../../components/reusableComponent/TableModal";
import { useGetEmailFirstResponseClickToExpandQuery } from "../../../../Services/dashboard/dashboard";

interface IPROPS {
  overview?: {
    active?: number;
    closed?: number;
    wip?: number;
    reopen?: number;
  };
  startDate?: string;
  endDate?: string;
  teamId?: string | null;
}

const CONFIG = [
  { key: "active", label: "Active", color: "#0d6efd" },
  { key: "wip", label: "WIP", color: "#ffc107" },
  { key: "closed", label: "Closed", color: "#198754" },
  { key: "reopen", label: "Reopen", color: "#fd7e14" },
];

const EmailPieChart: React.FC<IPROPS> = ({ overview, startDate, endDate, teamId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  const [allZero, setAllZero] = useState(false);
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [selectedStatusType, setSelectedStatusType] = useState<string | null>(null);
  const [clickToExpandPage, setClickToExpandPage] = useState(1);
  const totalTickets = overview ? Object.values(overview).reduce((total: number, count: any) => total + (Number(count) || 0), 0) : 0;
  const PieChartContainer = PieChart as any;
  const PieContainer = Pie as any;
  const CellContainer = Cell as any;
  const ResponsiveChartContainer = ResponsiveContainer as any;
  const LegendContainer = Legend as any;
  const TooltipContainer = Tooltip as any;

  useEffect(() => {
    if (!overview) {
      setAllZero(true);
      setData([]);
      return;
    }

    const normalized = CONFIG.map(item => ({
      name: item.label,
      value: Number(overview[item.key as keyof typeof overview] ?? 0),
      fill: item.color,
      key: item.key,
    }));

    setAllZero(normalized.every(d => d.value === 0));
    setActiveIndex(undefined);
    setData(normalized);
  }, [overview]);

  const clickToExpandParams =
    tableModalOpen && selectedStatusType && teamId && startDate && endDate
      ? {
          graph_name: "tickets_by_status_type",
          value: selectedStatusType,
          start_date: startDate,
          end_date: endDate,
          team_id: teamId,
          page: clickToExpandPage,
          page_size: 10,
        }
      : null;

  const { data: clickToExpandData, isLoading: isClickToExpandLoading } =
    useGetEmailFirstResponseClickToExpandQuery(
      clickToExpandParams ?? {
        graph_name: "",
        value: "",
        start_date: "",
        end_date: "",
        team_id: "",
      },
      { skip: !clickToExpandParams }
    );

  const clickToExpandRows = useMemo(() => {
    if (!clickToExpandData) return [];
    const raw =
      (clickToExpandData as any)?.results ??
      (clickToExpandData as any)?.data ??
      clickToExpandData;
    return Array.isArray(raw) ? raw : [];
  }, [clickToExpandData]);

  const clickToExpandPagination = useMemo(() => {
    const d = clickToExpandData as any;
    if (!d) return null;
    const count = d.count ?? (Array.isArray(d.results) ? d.results.length : 0);
    const totalPages = d.total_pages ?? 1;
    const page = d.page ?? clickToExpandPage;
    const pageSize = d.page_size ?? 10;
    return { totalCount: count, currentPage: page, totalPages, pageSize };
  }, [clickToExpandData, clickToExpandPage]);

  useEffect(() => {
    if (tableModalOpen) setClickToExpandPage(1);
  }, [selectedStatusType]);

  const handleSegmentClick = (index: number) => {
    if (!teamId || !startDate || !endDate) return;
    const key = CONFIG[index]?.key;
    if (key) {
      setSelectedStatusType(key);
      setTableModalOpen(true);
      setClickToExpandPage(1);
    }
  };

  const CustomLegend = () => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map((item, index) => {
          const percentage = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
          const isActive = activeIndex === index;
          
          return (
            <div
              key={item.name}
              role="button"
              onClick={() => handleSegmentClick(index)}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              style={{
                padding: "8px 12px",
                borderRadius: 6,
                cursor: teamId && startDate && endDate ? "pointer" : "default",
                background: isActive ? `${item.fill}10` : "#f8fafc",
                border: `1.5px solid ${isActive ? item.fill : 'transparent'}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                transform: isActive ? "scale(1.02)" : "scale(1)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: item.fill,
                    boxShadow: isActive ? `0 0 8px ${item.fill}80` : 'none',
                  }}
                />
                <span style={{ 
                  color: "#374151", 
                  fontWeight: isActive ? "600" : "500",
                  fontSize: "13px",
                  marginRight: "5px"
                }}>
                  {item.name}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ 
                  color: "#9ca3af", 
                  fontSize: "12px",
                  fontWeight: "500"
                }}>
                  {percentage}%
                </span>
                <strong style={{ 
                  color: isActive ? item.fill : "#1f2937", 
                  fontSize: "14px",
                  minWidth: "30px",
                  textAlign: "right"
                }}>
                  {item.value}
                </strong>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0];
    const total = payload[0].payload.value;

    return (
      <div
        style={{
          background: "white",
          padding: "8px 12px",
          borderRadius: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          border: `2px solid ${data.payload.fill}`,
        }}
      >
        <div style={{ 
          color: data.payload.fill, 
          fontWeight: "600",
          fontSize: "13px",
          marginBottom: 2
        }}>
          {data.name}
        </div>
        <div style={{ color: "#6b7280", fontSize: "12px" }}>
          {total} tickets
        </div>
      </div>
    );
  };

  return (
    <div className="card card-flush h-lg-100">
      <div className="card-header pt-5">
        <h3 className="card-title">
          <span className="fs-2 fw-bold text-gray-800">
            {`Ticket by email: ${totalTickets}`}
          </span>
        </h3>
      </div>

      <div className="card-body pt-5">
        {allZero ? (
          <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmptyRows value="" />
          </div>
        ) : (
          <ResponsiveChartContainer width="100%" height={240}>
            <PieChartContainer>
              <PieContainer
                data={data}
                cx="50%"
                cy="50%"
                innerRadius="45%"
                outerRadius="75%"
                dataKey="value"
                onClick={(_: any, index: number) => handleSegmentClick(index)}
                onMouseEnter={(_: any, index: number) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(undefined)}
                paddingAngle={3}
                animationBegin={0}
                animationDuration={600}
              >
                {data.map((d, i) => (
                  <CellContainer 
                    key={i} 
                    fill={d.fill}
                    strokeWidth={activeIndex === i ? 4 : 2}
                    stroke={activeIndex === i ? "white" : "#ffffff"}
                    style={{
                      filter: activeIndex === i 
                        ? `drop-shadow(0 4px 12px ${d.fill}60)` 
                        : 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                      transition: 'all 0.3s ease',
                      transform: activeIndex === i ? 'scale(1.05)' : 'scale(1)',
                      transformOrigin: 'center',
                    }}
                  />
                ))}
              </PieContainer>

              <TooltipContainer content={<CustomTooltip />} />
              <LegendContainer
                content={<CustomLegend />}
                layout="vertical"
                align="right"
                verticalAlign="middle"
                wrapperStyle={{ paddingLeft: 20 }}
              />
            </PieChartContainer>
          </ResponsiveChartContainer>
        )}
      </div>

      <TableModal
        isOpen={tableModalOpen}
        onRequestClose={() => {
          setTableModalOpen(false);
          setSelectedStatusType(null);
          setClickToExpandPage(1);
        }}
        title={
          selectedStatusType
            ? `Tickets by status – ${CONFIG.find(c => c.key === selectedStatusType)?.label ?? selectedStatusType}`
            : "Leads"
        }
        contentLabel="Tickets by status click-to-expand"
        emptyMessage="No emails in this segment."
        data={clickToExpandRows}
        isLoading={isClickToExpandLoading}
        getRowKey={(row: any, idx) => row?.thread_id ?? idx}
        maxWidth="1200px"
        onRowClick={(row: any) => {
          const threadId = row?.thread_id;
          if (threadId != null) {
            setTableModalOpen(false);
            setSelectedStatusType(null);
            navigate(`/email/inbox/${threadId}`);
          }
        }}
        totalCount={clickToExpandPagination?.totalCount}
        currentPage={clickToExpandPagination?.currentPage}
        totalPages={clickToExpandPagination?.totalPages}
        pageSize={clickToExpandPagination?.pageSize}
        onPageChange={setClickToExpandPage}
        columns={[
          { label: "Subject", render: (row: any) => row?.subject ?? "—" },
          { label: "Contact Name", render: (row: any) => row?.contact_name ?? "—" },
          { label: "Contact Email", render: (row: any) => row?.contact_email ?? "—" },
          { label: "Contact Phone", render: (row: any) => row?.contact_phone ?? "—" },
          {
            label: "Created",
            render: (row: any) => {
              const raw = row?.created;
              if (!raw) return "—";
              const d = new Date(raw);
              if (Number.isNaN(d.getTime())) return "—";
              const dd = String(d.getDate()).padStart(2, "0");
              const mm = String(d.getMonth() + 1).padStart(2, "0");
              const yyyy = d.getFullYear();
              const hh = String(d.getHours()).padStart(2, "0");
              const min = String(d.getMinutes()).padStart(2, "0");
              const ss = String(d.getSeconds()).padStart(2, "0");
              return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
            },
          },
        ]}
      />
    </div>
  );
};

export default EmailPieChart;